
import React, { useRef, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

interface CustomCaptchaProps {
  onChange: (value: string) => void;
  value: string;
  onValidityChange: (isValid: boolean) => void;
}

const CustomCaptcha: React.FC<CustomCaptchaProps> = ({ onChange, value, onValidityChange }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [captchaText, setCaptchaText] = useState<string>("");
  
  // Generate random captcha text
  const generateCaptchaText = (): string => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let result = "";
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };
  
  // Draw captcha on canvas
  const drawCaptcha = (text: string) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw background
    ctx.fillStyle = "#f5f5f5";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add noise
    for (let i = 0; i < 100; i++) {
      ctx.fillStyle = `rgba(${Math.random() * 255},${Math.random() * 255},${Math.random() * 255},0.2)`;
      ctx.fillRect(
        Math.random() * canvas.width,
        Math.random() * canvas.height,
        2,
        2
      );
    }
    
    // Add lines
    for (let i = 0; i < 4; i++) {
      ctx.strokeStyle = `rgba(${Math.random() * 255},${Math.random() * 255},${Math.random() * 255},0.5)`;
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.stroke();
    }
    
    // Draw text
    const textWidth = canvas.width / text.length;
    for (let i = 0; i < text.length; i++) {
      ctx.font = `${16 + Math.random() * 10}px Arial`;
      ctx.fillStyle = `rgb(${Math.floor(Math.random() * 100)},${Math.floor(Math.random() * 100)},${Math.floor(Math.random() * 100)})`;
      ctx.textBaseline = "middle";
      
      const xPos = (i + 0.5) * textWidth;
      const yPos = canvas.height / 2 + Math.random() * 10 - 5;
      const rotation = (Math.random() - 0.5) * 0.4;
      
      ctx.save();
      ctx.translate(xPos, yPos);
      ctx.rotate(rotation);
      ctx.fillText(text[i], -ctx.measureText(text[i]).width / 2, 0);
      ctx.restore();
    }
  };
  
  // Generate new captcha
  const refreshCaptcha = () => {
    const newCaptcha = generateCaptchaText();
    setCaptchaText(newCaptcha);
    drawCaptcha(newCaptcha);
    onChange("");
    onValidityChange(false);
  };
  
  // Check if the entered value matches the captcha
  useEffect(() => {
    if (value && captchaText) {
      const isValid = value === captchaText;
      onValidityChange(isValid);
    } else {
      onValidityChange(false);
    }
  }, [value, captchaText, onValidityChange]);
  
  // Initialize captcha on mount
  useEffect(() => {
    refreshCaptcha();
  }, []);
  
  return (
    <div className="space-y-2">
      <Label htmlFor="captcha" className="font-medium">
        Enter the code shown below
      </Label>
      
      <div className="flex items-center space-x-2">
        <canvas 
          ref={canvasRef} 
          width={200} 
          height={60} 
          className="border rounded-md" 
        />
        <Button 
          type="button" 
          size="icon" 
          variant="ghost" 
          onClick={refreshCaptcha}
          aria-label="Refresh captcha"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
      
      <Input
        id="captcha"
        placeholder="Enter captcha text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2"
      />
      
      {value && value !== captchaText && (
        <FormMessage>Captcha does not match. Please try again.</FormMessage>
      )}
    </div>
  );
};

export default CustomCaptcha;
